/**
 * One-button admin page that triggers the read-only inventory drift
 * diagnostic. Visit `/diagnostics/inventory-csv` while signed in as
 * admin → click "Download CSV" → browser downloads the file.
 *
 * Uses the existing JWT in localStorage so we don't have to fight
 * with cross-tab cookie auth. Same pattern as the rest of the app.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function InventoryDiagnosticPage() {
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(100);
  const [error, setError] = useState('');

  const download = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/admin/inventory-diagnose?limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      // Trigger a browser download from the blob response.
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename from Content-Disposition if present, else default.
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] :
        `inventory-diagnose-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded — share with support to diagnose.');
    } catch (e) {
      const detail = e?.response?.status === 403
        ? 'Admin/super-admin access required to run this diagnostic.'
        : (e?.response?.data?.detail || e?.message || 'Download failed');
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="inventory-diagnostic-page">
      <h1 className="text-2xl font-semibold mb-2">Inventory drift diagnostic</h1>
      <p className="text-sm text-wt-text-muted mb-6">
        Read-only walk through your recent Shopify orders. Per order it
        reports whether an Opswarex reservation was created, whether
        the SKU exists in local inventory, and the current `total_available`.
        Use this to root-cause the Cashfree-OCC inventory drift bug.
        <strong className="block mt-1 text-amber-700">
          No DB writes, no Shopify writes. Safe to run any time.
        </strong>
      </p>

      <Card className="p-6 space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wide text-wt-text-muted mb-1">
            Number of recent orders to scan
          </label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-32 px-3 py-2 rounded border border-wt-border bg-white text-wt-text-primary"
            data-testid="diag-limit-select"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>

        <Button
          onClick={download}
          disabled={loading}
          className="bg-wt-gold hover:bg-wt-gold/90 text-white"
          data-testid="diag-download-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {loading ? 'Generating…' : 'Download CSV'}
        </Button>

        {error && (
          <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3"
               data-testid="diag-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
            <span>{error}</span>
          </div>
        )}
      </Card>

      <div className="mt-6 text-sm text-wt-text-muted space-y-2">
        <div className="font-semibold text-wt-text-primary">What's in the CSV</div>
        <ul className="list-disc ml-5 space-y-1">
          <li><code>source_name</code> — which channel created the order (Cashfree-OCC vs native)</li>
          <li><code>reservation_count_for_order</code> — 0 means we never reserved stock</li>
          <li><code>skus_missing_from_local_inventory</code> — SKUs Shopify sent us that we don't recognise</li>
          <li><code>diagnosis</code> — auto-classification: OK / NO_RESERVATION_CREATED / SKU_MISSING_FROM_INVENTORY / FULFILLED_BUT_NOT_COMMITTED</li>
        </ul>
        <p className="pt-2">
          Open the CSV in Excel or Google Sheets, or just attach it to your
          next support message — we'll diagnose from the <code>diagnosis</code>
          column distribution alone.
        </p>
      </div>
    </div>
  );
}
